// Pure logic for the concierge to-do list — unit-tested; components stay thin.
// Spec: docs/superpowers/specs/2026-09-19-concierge-todo-task-model-design.md.
// Server dates: due dates are "YYYY-MM-DD"; closed_at (set when a task closes,
// per-instance) is a UTC-naive ISO datetime. "Today" is the UTC calendar day,
// the same one the server uses.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const UPCOMING_DAYS = 7;   // matches the backend's DUE_SOON_DAYS
const CLOSED = ['done', 'na'];

export function fmtDay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${MONTHS[m - 1]} ${d}`;
}

export function utcTodayIso(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

export function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function isOverdue(t, todayIso) {
  return !CLOSED.includes(t.status) && !!t.due_at && t.due_at < todayIso;
}

// One axis: time. Waiting is a badge on the row, not a bucket -- which is why
// an overdue Waiting task shows up under OVERDUE where the header counts it.
export function bucketTasks(tasks, todayIso) {
  const g = { overdue: [], upcoming: [], later: [], closed: [] };
  const horizon = addDays(todayIso, UPCOMING_DAYS);
  for (const t of tasks || []) {
    if (CLOSED.includes(t.status)) { g.closed.push(t); continue; }
    if (t.due_at < todayIso) g.overdue.push(t);
    else if (t.due_at <= horizon) g.upcoming.push(t);
    else g.later.push(t);
  }
  ['overdue', 'upcoming', 'later'].forEach(k => g[k].sort(
    (a, b) => a.due_at.localeCompare(b.due_at) || a.id - b.id));
  return g;
}

export function describeTask(t, { withNote = true } = {}) {
  const parts = [];
  if (t.status === 'na') parts.push('N/A');
  else if (t.status === 'done') {
    parts.push(`Done ${fmtDay(t.closed_at)}${t.closed_by_name ? ` · ${t.closed_by_name}` : ''}`);
  } else if (t.status === 'waiting') parts.push(`Follow up by ${fmtDay(t.due_at)}`);
  else parts.push(`Due ${fmtDay(t.due_at)}`);
  if (withNote && t.note) parts.push(t.note);
  return parts.join(' · ');
}

const EVENT_VERBS = {
  done: 'checked off',
  reopen: 'reopened',
  na: 'marked N/A',
  edit: 'edited',
  created: 'added',
  deleted: 'deleted',
  milestone_recorded: 'recorded',
  milestone_removed: 'un-recorded',
  milestone_edited: 'edited',
};

export function eventVerb(e) {
  return e.action === 'waiting'
    ? `marked waiting${e.detail?.waiting_on ? ` on ${e.detail.waiting_on}` : ''}`
    : (EVENT_VERBS[e.action] || e.action);
}

// One History line: "<date> · <actor or —> · <verb> <item label>".
export function describeEvent(e) {
  return `${fmtDay(e.created_at)} · ${e.actor_name || '—'} · ${eventVerb(e)} ${e.item_label}`;
}

// The To-do row's right-hand due column: "Oct 2 · 4d late" when overdue.
export function dueLabel(t, todayIso) {
  if (!isOverdue(t, todayIso)) return fmtDay(t.due_at);
  const late = Math.round((Date.parse(`${todayIso}T00:00:00Z`) - Date.parse(`${t.due_at}T00:00:00Z`)) / 86400000);
  return `${fmtDay(t.due_at)} · ${late}d late`;
}

export function summaryTemplate(prompts) {
  return (prompts || []).map(p => `${p.label}:\n`).join('\n');
}

export function groupByAthlete(items) {
  const groups = [];
  const index = new Map();
  for (const it of items || []) {
    if (!index.has(it.athlete_user_id)) {
      index.set(it.athlete_user_id, groups.length);
      groups.push({ athlete_user_id: it.athlete_user_id, athlete_name: it.athlete_name, items: [] });
    }
    groups[index.get(it.athlete_user_id)].items.push(it);
  }
  return groups;
}

export function todoErrorText(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return d?.message || fallback;
}
